import { useEffect, useRef } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { usePathname } from 'expo-router';

import Currency from 'components/currency';
import { isHydrationOrDehydrationInProgress } from 'hooks/use-cart-hydration.ts';
import { useCartContext } from 'state/cart';
import { selectors, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { primitive } from 'styles/constants';
import { logger } from 'utils/logger';
import { truncateServerOrderForLog } from 'utils/logger/utils';
import { routes } from 'utils/routing';

import { Container, Header, LineItem, TextDetails, Title, TitleContainer } from './styled';

const PriceMismatchInternalOnly = () => {
  const pathname = usePathname();
  const isCartRoute = pathname.startsWith(routes.cart);
  const enableInternalDiagnosticTools = useFlag(LaunchDarklyFlag.ENABLE_INTERNAL_DIAGNOSTIC_TOOLS);
  const hasLoggedRef = useRef(false);
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);

  const {
    requestedAmountCents,
    serverOrder,
    priceOrderFailure,
    isOrderPricingRetryInProgress,
    isOrderPriceLoading,
  } = useCartContext();

  const serverAmountCents = serverOrder?.cart.subTotalCents;
  const isPriceMismatch = serverAmountCents && serverAmountCents !== requestedAmountCents;
  const isPriceFailureFinal = priceOrderFailure && !isOrderPricingRetryInProgress;
  const isLoading = isOrderPriceLoading || isHydrationOrDehydrationInProgress;

  useEffect(() => {
    if (
      isCartRoute &&
      !isLoading &&
      (isPriceMismatch || isPriceFailureFinal) &&
      !hasLoggedRef.current
    ) {
      hasLoggedRef.current = true;
      logger.error({
        message: isPriceMismatch ? 'Price mismatch detected' : 'Pricing failure detected',
        requestedAmountCents,
        serverAmountCents,
        serverOrder: truncateServerOrderForLog(serverOrder),
        appliedLoyaltyRewards,
      });
    }
  }, [
    isCartRoute,
    isLoading,
    isPriceMismatch,
    isPriceFailureFinal,
    serverOrder,
    requestedAmountCents,
    serverAmountCents,
    appliedLoyaltyRewards,
  ]);

  if (isLoading) {
    return null;
  }

  if (!isCartRoute) {
    return null;
  }

  if (!isPriceMismatch && !isPriceFailureFinal) {
    return null;
  }

  if (!enableInternalDiagnosticTools) {
    return null;
  }
  return (
    <Container>
      <Header>{t('forInternalUseOnly')}</Header>
      <LineItem>
        <TextDetails>RBI Order ID</TextDetails>
        <TextDetails>{serverOrder?.rbiOrderId}</TextDetails>
      </LineItem>
      <LineItem>
        <TextDetails>Status</TextDetails>
        <TextDetails>{serverOrder?.status}</TextDetails>
      </LineItem>
      {serverOrder?.delivery?.status && (
        <LineItem>
          <TextDetails>Delivery Status</TextDetails>
          <TextDetails>{serverOrder.delivery.status}</TextDetails>
        </LineItem>
      )}
      {isPriceMismatch && (
        <>
          <TitleContainer>
            <Icon variant="warning" color={primitive.$white} aria-hidden size="4" />
            <Title>{t('priceMismatchDetected')}</Title>
          </TitleContainer>
          <LineItem>
            <TextDetails>{t('requestedAmount')}</TextDetails>
            <Currency textProps={{ color: primitive.$white }} amount={requestedAmountCents} />
          </LineItem>
          <LineItem>
            <TextDetails>{t('subtotal')}</TextDetails>
            <Currency textProps={{ color: primitive.$white }} amount={serverAmountCents} />
          </LineItem>
        </>
      )}
      {priceOrderFailure && (
        <>
          <TitleContainer>
            <Icon variant="warning" color={primitive.$white} aria-hidden size="4" />
            <Title>Pricing Error</Title>
          </TitleContainer>
          <LineItem>
            <TextDetails>Error Type</TextDetails>
            {serverOrder?.orderErrors?.orderInjectionErrors?.isStoreAvailabilityError && (
              <TextDetails>Store Availability Error</TextDetails>
            )}
            {serverOrder?.orderErrors?.orderInjectionErrors?.isTimeoutError && (
              <TextDetails>Timeout Error</TextDetails>
            )}
            {serverOrder?.orderErrors?.orderInjectionErrors?.isMenuError && (
              <TextDetails>Menu Error</TextDetails>
            )}
            {serverOrder?.orderErrors?.orderInjectionErrors?.isConfigurationError && (
              <TextDetails>Configuration Error</TextDetails>
            )}
            {serverOrder?.orderErrors?.orderInjectionErrors?.isUnknownError && (
              <TextDetails>Unknown Error</TextDetails>
            )}
          </LineItem>
        </>
      )}
    </Container>
  );
};

export default PriceMismatchInternalOnly;
